import React, { useEffect, useRef, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import AppsIcon from '@atlaskit/icon/core/apps';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import type { TriggerProps } from '@atlaskit/popup';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import { RIBBON_DND_TILE_TYPE } from '../types';
import { isRibbonAppTileDndData } from '../utils';

const styles = cssMap({
	moreAppsTrigger: {
		paddingInline: '0',
	},
});

/**
 * Footer tile that switches between "More Apps" and a "Hide" drop target.
 *
 * When a drag is active, the tile displays "Hide" and becomes a drop target.
 * While the dragged app tile is over the target, the tile background changes
 * to provide a clear hover/over state.
 */
export function MoreAppsOrHideTile({
	isDragActive,
	moreLabel,
	hideLabel,
	testIdPrefix,
	onClick,
	triggerProps,
}: {
	hideLabel: string;
	isDragActive: boolean;
	moreLabel: string;
	onClick: () => void;
	testIdPrefix: string;
	triggerProps: TriggerProps;
}): React.JSX.Element {
	const ref = useRef<HTMLDivElement | null>(null);
	const [isDraggedOver, setIsDraggedOver] = useState(false);

	useEffect(() => {
		// If the overall drag ends (or was cancelled), ensure we clear the "over" state.
		if (!isDragActive) {
			setIsDraggedOver(false);
		}
	}, [isDragActive]);

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		return dropTargetForElements({
			element,
			// Keep the drop target registered at all times; `canDrop` ensures we only accept
			// ribbon app tiles, and the UI only shows "Hide" while a drag is active.
			canDrop({ source }) {
				return isRibbonAppTileDndData(source.data);
			},
			getData() {
				// This marker is used by the monitor hook to interpret the drop as a "hide" action.
				return { type: RIBBON_DND_TILE_TYPE.HIDE_TILE };
			},
			onDragEnter() {
				setIsDraggedOver(true);
			},
			onDragLeave() {
				setIsDraggedOver(false);
			},
			onDrop() {
				setIsDraggedOver(false);
			},
		});
	}, []);

	const handleMoreAppsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		onClick();
	};

	return (
		<Box ref={ref} testId={`${testIdPrefix}--${isDragActive ? 'hide' : 'more-apps'}-tile`}>
			{isDragActive ? (
				// Hide app tile
				<Stack alignInline="center" space="space.075">
					<Tile
						label={hideLabel}
						size="medium"
						backgroundColor={
							// Provide a clear "drag over" affordance when the dragged tile is over the target.
							isDragActive && isDraggedOver
								? 'color.background.accent.gray.subtler'
								: 'color.background.accent.gray.subtlest'
						}
					>
						<EyeOpenStrikethroughIcon
							label={hideLabel}
							color={isDragActive ? 'var(--ds-icon-accent-red)' : undefined}
						/>
					</Tile>
					<Text size="small" align="center" color="color.text.subtle">
						{hideLabel}
					</Text>
				</Stack>
			) : (
				<Pressable
					onClick={handleMoreAppsClick}
					testId={`${testIdPrefix}--more-apps-button`}
					xcss={styles.moreAppsTrigger}
					{...triggerProps}
				>
					<Stack alignInline="center" space="space.075">
						<Tile
							label={moreLabel}
							size="medium"
							backgroundColor={'color.background.accent.gray.subtlest'}
						>
							<AppsIcon label={moreLabel} color={token('color.icon')} />
						</Tile>
						<Text size="small" align="center" color="color.text.subtle">
							{moreLabel}
						</Text>
					</Stack>
				</Pressable>
			)}
		</Box>
	);
}
